import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { companyAssessmentCertificatesService } from '../api/companyAssessmentCertificatesService';

function CompanyAssessmentCertificates() {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [userCanIssue, setUserCanIssue] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    company_id: '',
    assessment_category: 'health_safety',
    level_achieved: '',
    assessment_date: new Date().toISOString().split('T')[0],
    assessor_notes: ''
  });

  const categoryLabels = {
    health_safety: 'Health & Safety',
    quality: 'Quality',
    food_hygiene: 'Food Hygiene',
    workers_care: 'Workers Care',
    work_assessments: 'Work Assessments'
  };

  const levelDescriptions = {
    1: 'Level 1 - Initial/Minimum Requirements',
    2: 'Level 2 - Developing Standards',
    3: 'Level 3 - Established Practices',
    4: 'Level 4 - Advanced Compliance',
    5: 'Level 5 - Excellence/Best Practice'
  };

  useEffect(() => {
    checkPermission();
  }, []);

  const checkPermission = async () => {
    try {
      const userRole = localStorage.getItem('userRole');
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');

      if (userRole === 'super_admin') {
        setCurrentUser({ ...storedUser, role: userRole });
        setUserCanIssue(true);
        fetchCertificates();
        fetchCompanies();
        return;
      }

      // Check can_issue_company_assessments permission
      try {
        const response = await axiosClient.get('/auth/me');
        setCurrentUser(response.data);

        const hasPermission = response.data.can_issue_company_assessments || response.data.role === 'super_admin';
        setUserCanIssue(hasPermission);

        if (hasPermission) {
          fetchCertificates();
          fetchCompanies();
        }
      } catch (apiErr) {
        console.log('Using localStorage for permission check');
        setCurrentUser({ ...storedUser, role: userRole });

        const hasPermission = storedUser.can_issue_company_assessments || userRole === 'super_admin';
        setUserCanIssue(hasPermission);

        if (hasPermission) {
          fetchCertificates();
          fetchCompanies();
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Permission check failed:', err);
      setLoading(false);
    }
  };

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const response = await companyAssessmentCertificatesService.list();
      setCertificates(response.data);
    } catch (err) {
      console.error('Error fetching company assessment certificates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axiosClient.get('/companies');
      setCompanies(response.data.filter(c => !c.is_third_party_admin));
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        company_id: parseInt(formData.company_id),
        assessment_category: formData.assessment_category,
        level_achieved: parseInt(formData.level_achieved),
        assessment_date: new Date(formData.assessment_date).toISOString(),
        assessor_notes: formData.assessor_notes || null
      };

      await companyAssessmentCertificatesService.create(payload);

      setShowCreateForm(false);
      setFormData({
        company_id: '',
        assessment_category: 'health_safety',
        level_achieved: '',
        assessment_date: new Date().toISOString().split('T')[0],
        assessor_notes: ''
      });
      fetchCertificates();
      alert('Company assessment certificate issued successfully!');
    } catch (err) {
      console.error('Error creating company assessment certificate:', err);
      alert('Failed to issue certificate: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this company assessment certificate?')) {
      return;
    }

    try {
      await companyAssessmentCertificatesService.delete(id);
      fetchCertificates();
      alert('Certificate deleted successfully');
    } catch (err) {
      console.error('Error deleting certificate:', err);
      alert('Failed to delete certificate: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      1: '#dc3545', // Red
      2: '#fd7e14', // Orange
      3: '#ffc107', // Yellow
      4: '#28a745', // Green
      5: '#007bff'  // Blue (Excellence)
    };
    return colors[level] || '#6c757d';
  };

  const isExpired = (expiryDate) => {
    return new Date(expiryDate) < new Date();
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading company assessment certificates...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Company Assessment Certificates</h1>
          <p style={{ color: '#666', margin: 0 }}>
            Issue and manage company assessments across Health & Safety, Quality, Food Hygiene, Workers Care, and Work Assessments
          </p>
        </div>
        {userCanIssue && (
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showCreateForm ? 'Cancel' : '+ Issue New Assessment Certificate'}
          </button>
        )}
      </div>

      {/* Subscription Gate */}
      {!userCanIssue && !loading && (
        <div style={{
          background: 'linear-gradient(135deg, #43cea2 0%, #185a9d 100%)',
          borderRadius: '12px',
          padding: '40px',
          color: 'white',
          textAlign: 'center',
          marginTop: '40px',
          boxShadow: '0 10px 40px rgba(67, 206, 162, 0.3)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>--</div>
          <h2 style={{ margin: '0 0 15px 0', fontSize: '28px' }}>Company Assessment Certificates</h2>
          <p style={{ fontSize: '16px', marginBottom: '30px', opacity: 0.9, maxWidth: '600px', margin: '0 auto 30px' }}>
            Upgrade your subscription to unlock the ability to issue Company Assessment certificates across multiple categories including Health & Safety, Quality, and Food Hygiene.
          </p>
          <div style={{
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '30px',
            maxWidth: '500px',
            margin: '0 auto 30px'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '18px' }}>Features include:</h3>
            <ul style={{ textAlign: 'left', listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>Issue unlimited assessment certificates</li>
              <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>Multi-category assessments (Health & Safety, Quality, Food Hygiene)</li>
              <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>5-level achievement tracking</li>
              <li style={{ padding: '8px 0' }}>Professional certificate templates</li>
            </ul>
          </div>
          <button
            onClick={() => window.location.href = 'mailto:sales@icsc.org?subject=Company Assessment Certificate Subscription Inquiry'}
            style={{
              padding: '15px 40px',
              background: 'white',
              color: '#185a9d',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          >
            Contact Sales to Upgrade
          </button>
        </div>
      )}

      {showCreateForm && userCanIssue && (
        <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '4px', marginBottom: '20px' }}>
          <h2>Issue New Company Assessment Certificate</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Company *</label>
                <select
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value="">Select a company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Assessment Category *</label>
                <select
                  name="assessment_category"
                  value={formData.assessment_category}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  {Object.entries(categoryLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Level Achieved (1-5) *</label>
                <select
                  name="level_achieved"
                  value={formData.level_achieved}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value="">Select level</option>
                  {[1, 2, 3, 4, 5].map(level => (
                    <option key={level} value={level}>{levelDescriptions[level]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Assessment Date *</label>
                <input
                  type="date"
                  name="assessment_date"
                  value={formData.assessment_date}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Assessor Notes (Optional)</label>
                <textarea
                  name="assessor_notes"
                  value={formData.assessor_notes}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Additional observations, recommendations, or comments..."
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Issue Certificate
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {userCanIssue && (
        <div style={{ backgroundColor: 'white', borderRadius: '4px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h2>Issued Assessment Certificates</h2>
          {certificates.length === 0 ? (
          <p style={{ color: '#666' }}>No company assessment certificates issued yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>Certificate No</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Company</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Level</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Assessment Date</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Expiry Date</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {certificates.map(cert => (
                <tr key={cert.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px', fontSize: '13px', fontFamily: 'monospace' }}>{cert.certificate_number}</td>
                  <td style={{ padding: '12px' }}>{cert.company_name}</td>
                  <td style={{ padding: '12px' }}>{categoryLabels[cert.assessment_category]}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      background: getLevelColor(cert.level_achieved) + '20',
                      color: getLevelColor(cert.level_achieved)
                    }}>
                      Level {cert.level_achieved}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>{new Date(cert.assessment_date).toLocaleDateString()}</td>
                  <td style={{ padding: '12px' }}>{new Date(cert.expiry_date).toLocaleDateString()}</td>
                  <td style={{ padding: '12px' }}>
                    {isExpired(cert.expiry_date) ? (
                      <span style={{ color: '#dc3545', fontWeight: '500' }}>Expired</span>
                    ) : (
                      <span style={{ color: '#28a745', fontWeight: '500' }}>Valid</span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      {currentUser?.role === 'super_admin' && (
                        <button
                          onClick={() => handleDelete(cert.id)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}
    </div>
  );
}

export default CompanyAssessmentCertificates;
